import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography
} from "@mui/material";
import DateTimeInput from "../../components/DateTimeInput/DateTimeInput";
import HTMLEditor from "../../components/HtmlEditor/HtmlEditor";
import validationsService from "../../services/validationService";
import { noop } from "../../utils/helpers";
import {
  upsertAnnouncement,
  setAttribute
} from "../../redux/actions/announcementsActions";
import { useTranslation } from "react-i18next";
import { filesPreloading } from "../../services/fileUploadService";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(theme => ({
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap-reverse",
    minHeight: 38, // fix for IE,
    minWidth: "100%",
    "& > button": {
      margin: theme.spacing(4, 0, 0, 3)
    }
  },
  label: {
    marginBottom: theme.spacing(1.5)
  },
  checkbox: {
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary
  }
}));

const AnnouncementsForm = ({
  upsertAnnouncement,
  onCancel,
  announcement,
  setAttribute,
  afterSubmit
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filesList, setFilesList] = useState([]);

  const [value, setValue] = useState(announcement.body);

  useEffect(() => {
    setAttribute("body", value);
  }, [value]);

  const saveAnnouncement = async (
    announcement,
    afterSubmit,
    isPublish = false
  ) => {
    const tmpObj = await filesPreloading(filesList, announcement.body);
    announcement.images = tmpObj.images;
    announcement.media = tmpObj.media;
    announcement.attachments = tmpObj.attachments;
    announcement.body = tmpObj.content;

    if (isPublish) {
      upsertAnnouncement(afterSubmit, announcement);
    } else {
      upsertAnnouncement(afterSubmit, announcement, "Draft");
    }
  };

  const error =
    !announcement.title ||
    !announcement.body ||
    (!announcement.id &&
      !validationsService.checkDateNotInThePast(announcement.postDate)) ||
    (announcement.removeOnEndDate &&
      !validationsService.checkFirstDateIsFirst(
        announcement.postDate,
        announcement.endDate
      ));

  return (
    <div data-qa="announcements-form">
      <TextField
        defaultValue={announcement.title}
        onChange={ev => {
          setAttribute("title", ev.currentTarget.value);
        }}
        fullWidth
        required
        variant="filled"
        hiddenLabel
        InputProps={{
          placeholder: t("announcements.announcementsForm.subjectPlaceholder")
        }}
      />
      <HTMLEditor
        value={value}
        onChange={e => {
          setValue(e);
        }}
        onFileAdd={(fileObj, tempLink, type) => {
          let list = [...filesList];
          list.push({ fileObj, tempLink, type });
          setFilesList(list);
        }}
      />
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        flexWrap="wrap"
      >
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          mb={3}
          minWidth={{ xs: 0, sm: 516 }}
          minHeight={102}
        >
          <Typography
            variant="subtitle1"
            color="textSecondary"
            className={classes.label}
          >
            {t("announcements.announcementsForm.postDate")}
          </Typography>
          <DateTimeInput
            value={announcement.postDate}
            onChange={val => {
              setAttribute("postDate", val);
            }}
            error={
              !announcement.id &&
              !validationsService.checkDateNotInThePast(announcement.postDate)
            }
            helperText="The Post Date must be a future date. Please check your date and try again."
          />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          mb={3}
          minWidth={{ xs: 0, sm: 516 }}
          minHeight={102}
        >
          <Typography
            variant="subtitle1"
            color="textSecondary"
            className={classes.label}
          >
            {t("announcements.announcementsForm.endDate")}
          </Typography>
          <FormControlLabel
            className={classes.checkbox}
            control={
              <Checkbox
                checked={announcement.removeOnEndDate || false}
                onChange={(ev, checked) =>
                  setAttribute("removeOnEndDate", checked)
                }
                color="primary"
              />
            }
            label={
              <Typography variant="body2" color="textSecondary">
                {t("announcements.announcementsForm.removeCheckbox")}
              </Typography>
            }
          />
          <DateTimeInput
            disabled={!announcement.removeOnEndDate}
            value={announcement.endDate}
            onChange={val => {
              setAttribute("endDate", val);
            }}
            error={
              announcement.removeOnEndDate &&
              !validationsService.checkFirstDateIsFirst(
                announcement.postDate,
                announcement.endDate
              )
            }
            helperText="The End Date must be after the Post Date. Please check your date and try again."
          />
        </Box>
      </Box>
      <div className={classes.actions}>
        <Button color="primary" onClick={onCancel}>
          {t("cancelBtn")}
        </Button>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => saveAnnouncement(announcement, afterSubmit)}
        >
          {t("announcements.announcementsForm.saveAsDraftBtn")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            saveAnnouncement(announcement, afterSubmit, true);
          }}
          disabled={error}
        >
          {t("announcements.announcementsForm.publishBtn")}
        </Button>
      </div>
    </div>
  );
};

AnnouncementsForm.propTypes = {
  singleDateRow: PropTypes.bool,
  onCancel: PropTypes.func,
  afterSubmit: PropTypes.func
};

AnnouncementsForm.defaultProps = {
  singleDateRow: false,
  onCancel: noop,
  afterSubmit: noop
};

const mapStateToProps = state => ({
  announcement: state.announcementsState.announcement,
  error: state.announcementsState.error
});

export default compose(
  connect(mapStateToProps, {
    upsertAnnouncement,
    setAttribute
  })
)(AnnouncementsForm);
